/**
 * Rotated text items (a 90° "NIH-PA Author Manuscript" strip, an arXiv id in
 * the margin, the y-axis title of a chart).
 *
 * PDF.js reports every item as origin (e, f) plus an advance width along its
 * own baseline. For a rotated item that width does not run to the right, so
 * reading x / width as a horizontal extent invents a line that reaches into
 * the body column and gets grouped with whatever paragraph line happens to
 * share its origin's y. layout.ts therefore takes rotated items out before
 * line building and gives them boxes of their own, computed here.
 *
 * Rotation is the direction of the baseline (a, b) of the transform. A skew
 * (c ≠ 0 with b = 0, the synthetic italic of a PDF generator) is not a rotation.
 *
 * Pure functions, unit-tested in __tests__/rotation.test.ts.
 */

import type { Rect, TextItemDebug } from './types';

/** Baselines within this sine (≈ 1.15°) of horizontal count as horizontal. */
export const ROTATION_SIN_EPS = 0.02;
/** Glyph extents relative to the baseline, as in layout.ts. */
const ASCENT = 0.8;
const DESCENT = 0.25;

/** Baseline direction of an item in degrees, counter-clockwise, in (-180, 180]. */
export function itemAngle(item: Pick<TextItemDebug, 'transform'>): number {
  const [a, b] = item.transform;
  if (!Number.isFinite(a) || !Number.isFinite(b) || (a === 0 && b === 0)) return 0;
  const deg = (Math.atan2(b, a) * 180) / Math.PI;
  return Math.round(deg * 100) / 100 || 0;
}

/** True when the item's baseline is not horizontal left-to-right (rotated, vertical or upside down). */
export function isRotatedItem(item: Pick<TextItemDebug, 'transform'>): boolean {
  const [a, b] = item.transform;
  const scale = Math.hypot(a, b);
  if (!(scale > 0)) return false;
  return Math.abs(b) > ROTATION_SIN_EPS * scale || a < 0;
}

/**
 * Axis-aligned box of a rotated item: its origin, advanced by `width` along
 * the baseline, widened by ascent / descent along the baseline's normal.
 */
export function rotatedItemBox(item: Pick<TextItemDebug, 'transform' | 'width' | 'fontSize'>): Rect {
  const [a, b, , , e, f] = item.transform;
  const scale = Math.hypot(a, b) || 1;
  const ux = a / scale;
  const uy = b / scale;
  // Normal pointing "up" from the baseline (the baseline turned by +90°).
  const nx = -uy;
  const ny = ux;
  const fs = item.fontSize;
  const ends: Array<[number, number]> = [
    [e, f],
    [e + ux * item.width, f + uy * item.width],
  ];
  const xs: number[] = [];
  const ys: number[] = [];
  for (const [px, py] of ends) {
    xs.push(px + nx * ASCENT * fs, px - nx * DESCENT * fs);
    ys.push(py + ny * ASCENT * fs, py - ny * DESCENT * fs);
  }
  const x0 = Math.min(...xs);
  const y0 = Math.min(...ys);
  const r = (v: number) => Math.round(v * 100) / 100;
  return { x: r(x0), y: r(y0), width: r(Math.max(...xs) - x0), height: r(Math.max(...ys) - y0) };
}

/** Distance of the item's origin from the line through the origin along its baseline direction's normal. */
export function baselineOffset(item: Pick<TextItemDebug, 'transform'>): number {
  const [a, b, , , e, f] = item.transform;
  const scale = Math.hypot(a, b) || 1;
  return (-b / scale) * e + (a / scale) * f;
}

/** Position of the item's origin along its baseline direction. */
export function baselinePosition(item: Pick<TextItemDebug, 'transform'>): number {
  const [a, b, , , e, f] = item.transform;
  const scale = Math.hypot(a, b) || 1;
  return (a / scale) * e + (b / scale) * f;
}
